import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import StreamlineModal from "../components/StreamlineModal";
import TailorModal from "../components/TailorModal";
import { ReactComponent as FeatureBadgeIcon } from "../assets/images/feature-badge-icon.svg";
import feature_candidate_1 from '../assets/images/feature_candidate_1.jpg';
import feature_candidate_3 from '../assets/images/feature_candidate_3.jpg';
import feature_candidate_4 from '../assets/images/feature_candidate_4.jpg';
import { ReactComponent as TestimonialQuote } from '../assets/images/testimonial_quote.svg';
import { ReactComponent as HeroCTAButton } from '../assets/images/Optimize_My_Resume.svg';
import { ReactComponent as HeroCTAButtonDesktop } from '../assets/images/Hero_CTA_button.svg';
import { ReactComponent as HeroHowItWorksButton } from '../assets/images/Hero_How_it_Works_WIP.svg';
import { ReactComponent as HeroHowItWorksWIPButton } from '../assets/images/How_It_Works.svg';
import HeroResumeGraphic from "../components/HeroResumeGraphic";
import { ReactComponent as ArrowIcon } from '../assets/images/arrow.svg';
import '../styles/homepage.css';

/** Served from public/videos (stable URL). Override with REACT_APP_HOW_IT_WORKS_VIDEO_URL for CDN. */
const HOW_IT_WORKS_VIDEO_SRC =
  process.env.REACT_APP_HOW_IT_WORKS_VIDEO_URL ||
  `${process.env.PUBLIC_URL || ''}/videos/GimmeResume.mp4`;

const BETA_TESTING = (process.env.REACT_APP_BETA_TESTING || 'false').toLowerCase() === 'true';

/** Streamline card: set enabled false to show a single list price again. */
const STREAMLINE_PRICE_PROMO = {
  enabled: true,
  current: '$9.99',
  compareAt: '$19.99',
};

function StreamlinePrice() {
  const { enabled, current, compareAt } = STREAMLINE_PRICE_PROMO;
  if (!enabled) {
    return <div className="price">{current}</div>;
  }
  return (
    <div className="price price--promo">
      <span className="price-amount">{current}</span>
      <span className="price-caption">
        <s className="price-compare-at">{compareAt}</s>
      </span>
    </div>
  );
}

// FAQ data - easily updatable
const FAQ_DATA = [
  {
    id: 1,
    question: "Why does GimmeResume succeed where ChatGPT fails?",
    answer: "GimmeResume is a series of specialized AI/ML models that understands the job market for every profession, knows how to write for recruiters, and which in-demand keywords are missing from your resume.\n\nChatGPT is a general-purpose model that everyone (including our competitors) are using to write resumes. It doesn't know how to write for recruiters and doesn't know the in-demand keywords."
  },
  {
    id: 2,
    question: "How is this different from competitors?",
    answer: "GimmeResume is a series of specialized AI/ML Models that's: \n\n- Shaped by hundreds of recruiters. \n- Helped 1000s of users land their next role. \n- No hallucinations. \n- Understands the entire job market. \n- No subscriptions, no commitments. \n\n Our competitors basically just send your \nresume to ChatGPT to write it for you."
  },
  {
    id: 3,
    question: "When should I use the Streamline Resume feature?",
    answer: "The Streamline Resume feature is for when you want to optimize your resume for the entire job market and shows you the highest-demand skills that are missing from your resume so you can either add them or upskill."
  },
  {
    id: 4,
    question: "When should I use the Tailor Resume feature?",
    answer: "The Tailor Resume feature is for when you want to optimize your resume for a specific job posting. \n\nEach job posting has unique keywords, so tailoring is essential for maximum ATS compatibility."
  },
  {
    id: 5,
    question: "How long does it take to optimize my resume?",
    answer: "You can get your optimized resume in under 60 seconds. Simply upload your current resume and the job description, and our AI will analyze and optimize it instantly."
  },
  {
    id: 6,
    question: "Is my data secure and private?",
    answer: "We will never sell your data to third parties or use it for any other purpose than to optimize resumes, while other sites sell your data to advertisers and 3rd parties."
  },
  {
    id: 7,
    question: "Can I use this for multiple job applications?",
    answer: "Absolutely! Streamline Resume surfaces in-demand keywords from the latest job postings and Tailor Resume lets you deeply optimize your resume for any job description."
  },
  {
    id: 8,
    question: "How do I know this works?",
    answer: "We're confident in our AI's ability to improve your resume's ATS compatibility after testing it with thousands of users.\n\nYou can make final edits before downloading, and our system shows you exactly which keywords should be added or upskilled on."
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState(null);
  const [error, setError] = useState(null);
  const [betaInfo, setBetaInfo] = useState({ promoCode: '', email: '' });
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const [file, setFile] = useState(null);
  const optimizeRef = useRef(null);
  const howItWorksRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isVertical, setIsVertical] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [videoLoadError, setVideoLoadError] = useState(false);
  const videoRef = useRef(null);
  useEffect(() => {
    // Debounce function to limit how often checkMobile runs
    let resizeTimer;

    const checkMobile = () => {
      const mobile = (window.innerWidth >= 320 && window.innerWidth <= 480)
        ||
        (window.innerWidth >= 480 && window.innerWidth <= 768);
      const vertical = (window.innerWidth <= 1024)
      setIsVertical(vertical);
      if (!activeModal && !mobile) {
        setActiveModal('streamline');
      }
      setIsMobile(mobile);
    };

    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkMobile, 100); // Wait 100ms after last resize event
    };

    // Check on initial load
    checkMobile();

    // Add debounced event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  const scrollToOptimize = () => {
    if (optimizeRef.current) {
      optimizeRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openHowItWorksVideo = () => {
    window.plausible('How It Works Click', { props: { location: 'hero' } });
    setVideoLoadError(false);
    setIsVideoOpen(true);
  };

  const closeHowItWorksVideo = () => {
    setIsVideoOpen(false);
    setVideoLoadError(false);
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (e) {
        // noop
      }
    }
  };

  useEffect(() => {
    if (!isVideoOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeHowItWorksVideo();
    };
    window.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isVideoOpen]);

  const openModal = (modalName) => {
    setActiveModal(modalName);
    setError(null);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const toggleFaq = (id) => {
    if (!expandedFaqs[id]) {
      window.plausible('FAQ Click', { props: { faq_id: String(id) } });
    }
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleSubmitWithFormData = async (data) => {
    try {
      setError(null);

      // Validate beta data if in beta testing mode
      if (BETA_TESTING) {
        if (!betaInfo || !betaInfo.promoCode || !betaInfo.email) {
          setError('Please provide both promo code and email');
          return;
        }
        data.append("promoCode", betaInfo.promoCode);
        data.append("email", betaInfo.email);
        data.append("activeModal", activeModal);
        data.append("intentID", "promo");
      } else {
        data.append("promoCode", "");
        data.append("email", "");
        data.append("activeModal", activeModal);
      }

      // Convert FormData to a serializable format for navigation
      const formDataObj = {};
      const files = {};

      for (let [key, value] of data.entries()) {
        if (value instanceof File) {
          files[key] = {
            name: value.name,
            size: value.size,
            type: value.type,
            lastModified: value.lastModified
          };
          formDataObj[key] = value;
        } else {
          formDataObj[key] = value;
        }
      }

      navigate('/payment', {
        state: {
          formDataEntries: Array.from(data.entries()),
          activeModal: activeModal,
          betaInfo: betaInfo
        }
      });
    } catch (error) {
      console.error("Error in handleSubmitWithFormData:", error);
      setError('Failed to process form submission');
    }
  };

  return (
    <div className="homepage">

      <div className="section hero-section">
        <div className="feature-1-decoration"></div>
        <div className="feature-2-decoration"></div>
        <div className="hero-content">
          <div className="badge">
            <span className="badge-icon"></span>
            <h6 className="badge-text">Market-Driven Resume Optimizer</h6>
          </div>

          <p className="hero-title">Rise Above Others.<br />Get the Interview.<br />Get Hired.</p>
          {(!isMobile || !isVertical) && (
            <h6 className="hero-description">
              {/* Supercharge your resume with the intelligence of the entire job market in less than 60 seconds, boosting your chances of getting an interview by 90%<br /><br />

            *Streamline your resume with in-demand skills from all recent job postings or<br />

            *Tailor your resume to a specific job posting<br /><br /> */}

              {/* Our AI/ML system is trained on millions of job descriptions, helping you surface <b>every</b> in-demand <skill>skill</skill> that boosts your chances of getting an interview.<br /><br /> */}

              {/* Upgrade your resume with a recruiter-optimized rewrite and a deep ATS keyword coverage report from the latest job postings.<br /><br /> */}

              Optimize your resume for the ATS and get to the recruiter.<br />

              Choose the service that fits your needs - no <br /> subscriptions, no commitments.

              <div className="hero-buttons">
                <button
                  className="hero-cta-button"
                  onClick={() => { window.plausible('CTA Click', { props: { location: 'hero' } }); scrollToOptimize(); }}
                >
                  Optimize My Resume Now
                  <ArrowIcon className="arrow-icon" />
                </button>

                <button
                  className="hero-how-it-works-button"
                  onClick={openHowItWorksVideo}
                >
                  Watch How It Works
                </button>
              </div>

            </h6>)}

        </div>

        <HeroResumeGraphic
          fromScore={62}
          toScore={75}
          chipA="PyTorch"
          chipB="Kubernetes"
          titleName="Jordan M."
          titleRole="AI Engineer"
        />
        {(isMobile && isVertical) && (
          <h6 className="hero-description">
            {/* Supercharge your resume with the intelligence of the entire job market in less than 60 seconds, boosting your chances of getting an interview by 90%<br /><br />

            *Streamline your resume with in-demand skills from all recent job postings or<br />

            *Tailor your resume to a specific job posting<br /><br /> */}

            {/* Our AI/ML system is trained on millions of job descriptions, helping you surface <b>every</b> in-demand <skill>skill</skill> that boosts your chances of getting an interview.<br /><br /> */}

            {/* Upgrade your resume with a recruiter-optimized rewrite and a deep ATS keyword coverage report from the latest job postings.<br /><br /> */}

            Optimize your resume for the ATS and get to the recruiter.

            Choose the service that fits your needs - no subscriptions, no commitments.

            <div className="hero-buttons">
              <button
                className="hero-cta-button"
                onClick={() => { window.plausible('CTA Click', { props: { location: 'hero' } }); scrollToOptimize(); }}
              >
                Optimize My Resume Now
                <ArrowIcon className="arrow-icon" />
              </button>

              <button
                className="hero-how-it-works-button"
                onClick={openHowItWorksVideo}
              >
                Watch How It Works
              </button>
            </div>

          </h6>)}
      </div>


      <div className="section optimize-section" id="features">
        <h2 className="section-header">
          PICK THE SERVICE THAT FITS YOUR NEEDS.
        </h2>
        <h2 className="section-title" id="product" ref={optimizeRef}>
          Optimize My Resume Now
        </h2>
        <h3 className="section-subtitle mobile-only">Select from the two options below</h3>
        {isMobile && (
          <div className="optimize-workbench">
            <div className="card-container">
              <div
                className={`card pricing-card ${activeModal === 'streamline' ? 'selected' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => openModal('streamline')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openModal('streamline');
                  }
                }}
              >
                <div className="pricing-card-head">
                  <StreamlinePrice />
                  {!isMobile && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation(); // prevent double-trigger when clicking the button inside the card
                        openModal('streamline');
                      }}
                      className={`card-select-btn ${activeModal === 'streamline' ? 'selected' : ''}`}
                    >
                      {activeModal === 'streamline' ? (
                        <>
                          Selected
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path fillRule="evenodd" clipRule="evenodd" d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM14.707 8.707L9 14.414L5.293 10.707L6.707 9.293L9 11.586L13.293 7.293L14.707 8.707Z" fill="currentColor" />
                          </svg>
                        </>
                      ) : (
                        'Select'
                      )}
                    </button>)}
                </div>
                <h3 className="card-title">Streamline Resume</h3>
                <p className="card-description">Optimize your resume for the current job market</p>
                <ul className="feature-list">
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Get the top in-demand keywords from the latest job postings</span>
                  </li>
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Shows skills your resume is missing so you can add or upskill</span>
                  </li>
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Recruiter-optimized rewrite that doesn't read like AI</span>
                  </li>
                </ul>
                {isMobile && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent double-trigger when clicking the button inside the card
                      openModal('streamline');
                    }}
                    className={`card-select-btn ${activeModal === 'streamline' ? 'selected' : ''}`}
                  >
                    {activeModal === 'streamline' ? (
                      <>
                        Selected Below
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path fillRule="evenodd" clipRule="evenodd" d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM14.707 8.707L9 14.414L5.293 10.707L6.707 9.293L9 11.586L13.293 7.293L14.707 8.707Z" fill="currentColor" />
                        </svg>
                      </>
                    ) : (
                      'Select'
                    )}
                  </button>)}
              </div>
              {isMobile && activeModal === 'streamline' && (
                <div className="optimize-form-panel">
                  <StreamlineModal
                    file={file}
                    setFile={setFile}
                    isOpen={activeModal === 'streamline'}
                    onClose={closeModal}
                    onSubmit={(formData) => handleSubmitWithFormData(formData)}
                    betaTesting={BETA_TESTING}
                    setBetaInfo={setBetaInfo}
                    betaInfo={betaInfo}
                  />
                </div>
              )}
            </div>
            <div className="card-container">
              <div
                className={`card pricing-card ${activeModal === 'tailor' ? 'selected' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => openModal('tailor')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openModal('tailor');
                  }
                }}
              >
                <div className="pricing-card-head">
                  <div className="price">$1.99 <span className="price-period">per Job Description</span></div>
                  {!isMobile && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation(); // prevent double-trigger when clicking the button inside the card
                        openModal('tailor');
                      }}
                      className={`card-select-btn ${activeModal === 'tailor' ? 'selected' : ''}`}
                    >
                      {activeModal === 'tailor' ? (
                        <>
                          Selected
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path fillRule="evenodd" clipRule="evenodd" d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM14.707 8.707L9 14.414L5.293 10.707L6.707 9.293L9 11.586L13.293 7.293L14.707 8.707Z" fill="currentColor" />
                          </svg>
                        </>
                      ) : (
                        'Select'
                      )}
                    </button>
                  )}
                </div>
                <h3 className="card-title">Tailor Resume</h3>
                <p className="card-description">Customize your resume for a specific job listing</p>
                <ul className="feature-list">
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Captures ALL the keywords in the job description</span>
                  </li>
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Finds 1.9x more keywords than ChatGPT’s flagship model</span>
                  </li>
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Deep recruiter-optimized tailoring that doesn't read like AI</span>
                  </li>
                </ul>
                {isMobile && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent double-trigger when clicking the button inside the card
                      openModal('tailor');
                    }}
                    className={`card-select-btn ${activeModal === 'tailor' ? 'selected' : ''}`}
                  >
                    {activeModal === 'tailor' ? (
                      <>
                        Selected Below
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path fillRule="evenodd" clipRule="evenodd" d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM14.707 8.707L9 14.414L5.293 10.707L6.707 9.293L9 11.586L13.293 7.293L14.707 8.707Z" fill="currentColor" />
                        </svg>
                      </>
                    ) : (
                      'Select'
                    )}
                  </button>
                )}

              </div>
              {isMobile && activeModal === 'tailor' && (
                <div className="optimize-form-panel">
                  <TailorModal
                    file={file}
                    setFile={setFile}
                    isOpen={activeModal === 'tailor'}
                    onClose={closeModal}
                    onSubmit={(formData) => handleSubmitWithFormData(formData)}
                    betaTesting={BETA_TESTING}
                    setBetaInfo={setBetaInfo}
                    betaInfo={betaInfo}
                  />
                </div>
              )}
            </div>
            {!isMobile && (
              <div className="optimize-form-panel">
                <StreamlineModal
                  file={file}
                  setFile={setFile}
                  isOpen={activeModal === 'streamline'}
                  onClose={closeModal}
                  onSubmit={(formData) => handleSubmitWithFormData(formData)}
                  betaTesting={BETA_TESTING}
                  setBetaInfo={setBetaInfo}
                  betaInfo={betaInfo}
                />
                <TailorModal
                  file={file}
                  setFile={setFile}
                  isOpen={activeModal === 'tailor'}
                  onClose={closeModal}
                  onSubmit={(formData) => handleSubmitWithFormData(formData)}
                  betaTesting={BETA_TESTING}
                  setBetaInfo={setBetaInfo}
                  betaInfo={betaInfo}
                />
              </div>
            )}
          </div>
        )}
        {!isMobile && (
          <div className="optimize-workbench">
            <div className="card-container">
              <div
                className={`card pricing-card ${activeModal === 'streamline' ? 'selected' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => openModal('streamline')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openModal('streamline');
                  }
                }}
              >
                <div className="pricing-card-head">
                  <StreamlinePrice />
                  {!isMobile && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation(); // prevent double-trigger when clicking the button inside the card
                        openModal('streamline');
                      }}
                      className={`card-select-btn ${activeModal === 'streamline' ? 'selected' : ''}`}
                    >
                      {activeModal === 'streamline' ? (
                        <>
                          Selected
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path fillRule="evenodd" clipRule="evenodd" d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM14.707 8.707L9 14.414L5.293 10.707L6.707 9.293L9 11.586L13.293 7.293L14.707 8.707Z" fill="currentColor" />
                          </svg>
                        </>
                      ) : (
                        'Select'
                      )}
                    </button>)}
                </div>
                <h3 className="card-title">Streamline Resume</h3>
                <p className="card-description">Optimize your resume for the current job market</p>
                <ul className="feature-list">
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Get the top in-demand keywords from the latest job postings</span>
                  </li>
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Shows skills your resume is missing so you can add or upskill</span>
                  </li>
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Recruiter-optimized rewrite that doesn't read like AI</span>
                  </li>
                </ul>
                {isMobile && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent double-trigger when clicking the button inside the card
                      openModal('streamline');
                    }}
                    className={`card-select-btn ${activeModal === 'streamline' ? 'selected' : ''}`}
                  >
                    {activeModal === 'streamline' ? (
                      <>
                        Selected
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path fillRule="evenodd" clipRule="evenodd" d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM14.707 8.707L9 14.414L5.293 10.707L6.707 9.293L9 11.586L13.293 7.293L14.707 8.707Z" fill="currentColor" />
                        </svg>
                      </>
                    ) : (
                      'Select'
                    )}
                  </button>)}
              </div>
              {isMobile && activeModal === 'streamline' && (
                <div className="optimize-form-panel">
                  <StreamlineModal
                    file={file}
                    setFile={setFile}
                    isOpen={activeModal === 'streamline'}
                    onClose={closeModal}
                    onSubmit={(formData) => handleSubmitWithFormData(formData)}
                    betaTesting={BETA_TESTING}
                    setBetaInfo={setBetaInfo}
                    betaInfo={betaInfo}
                  />
                </div>
              )}

              <div
                className={`card pricing-card ${activeModal === 'tailor' ? 'selected' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => openModal('tailor')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openModal('tailor');
                  }
                }}
              >
                <div className="pricing-card-head">
                  <div className="price">$1.99 <span className="price-period">per Job Description</span></div>
                  {!isMobile && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation(); // prevent double-trigger when clicking the button inside the card
                        openModal('tailor');
                      }}
                      className={`card-select-btn ${activeModal === 'tailor' ? 'selected' : ''}`}
                    >
                      {activeModal === 'tailor' ? (
                        <>
                          Selected
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path fillRule="evenodd" clipRule="evenodd" d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM14.707 8.707L9 14.414L5.293 10.707L6.707 9.293L9 11.586L13.293 7.293L14.707 8.707Z" fill="currentColor" />
                          </svg>
                        </>
                      ) : (
                        'Select'
                      )}
                    </button>
                  )}
                </div>
                <h3 className="card-title">Tailor Resume</h3>
                <p className="card-description">Customize your resume for a specific job listing</p>
                <ul className="feature-list">
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Captures ALL the keywords in the job description</span>
                  </li>
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Finds 1.9x more keywords than ChatGPT’s flagship model</span>
                  </li>
                  <li className="feature-list-item-card">
                    <span className="feature-list-icon"></span>
                    <span>Deep recruiter-optimized tailoring that doesn't read like AI</span>
                  </li>
                </ul>
                {isMobile && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent double-trigger when clicking the button inside the card
                      openModal('tailor');
                    }}
                    className={`card-select-btn ${activeModal === 'tailor' ? 'selected' : ''}`}
                  >
                    {activeModal === 'tailor' ? (
                      <>
                        Selected
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path fillRule="evenodd" clipRule="evenodd" d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM14.707 8.707L9 14.414L5.293 10.707L6.707 9.293L9 11.586L13.293 7.293L14.707 8.707Z" fill="currentColor" />
                        </svg>
                      </>
                    ) : (
                      'Select'
                    )}
                  </button>
                )}

              </div>
              {isMobile && activeModal === 'tailor' && (
                <div className="optimize-form-panel">
                  <TailorModal
                    file={file}
                    setFile={setFile}
                    isOpen={activeModal === 'tailor'}
                    onClose={closeModal}
                    onSubmit={(formData) => handleSubmitWithFormData(formData)}
                    betaTesting={BETA_TESTING}
                    setBetaInfo={setBetaInfo}
                    betaInfo={betaInfo}
                  />
                </div>
              )}
            </div>
            {!isMobile && (
              <div className="optimize-form-panel">
                <StreamlineModal
                  file={file}
                  setFile={setFile}
                  isOpen={activeModal === 'streamline'}
                  onClose={closeModal}
                  onSubmit={(formData) => handleSubmitWithFormData(formData)}
                  betaTesting={BETA_TESTING}
                  setBetaInfo={setBetaInfo}
                  betaInfo={betaInfo}
                />
                <TailorModal
                  file={file}
                  setFile={setFile}
                  isOpen={activeModal === 'tailor'}
                  onClose={closeModal}
                  onSubmit={(formData) => handleSubmitWithFormData(formData)}
                  betaTesting={BETA_TESTING}
                  setBetaInfo={setBetaInfo}
                  betaInfo={betaInfo}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="white-bg-section-top" ref={howItWorksRef}>
        <div className="section-inner features-section-outer">
          <div className="section-inner features-section">
            <h2 className="section-header">
              WHAT MAKES THIS DIFFERENT?
            </h2>
            <h2 className="section-title">
              The intelligence of the entire job market.
            </h2>
            <p className="section-description">
              GimmeResume is a series of AI/ML models with an ever-evolving engine trained on millions of recent job postings. It knows the in-demand skills and keywords for every profession, and how to write for recruiters.
            </p>

            <div className="feature-container">
              <div className="feature-content">
                <div className="feature-badge">

                  <span>
                    <FeatureBadgeIcon className="feature-badge-icon" />
                    Solve for the ATS
                  </span>
                </div>
                <h3 className="feature-title">Why our solution works</h3>
                {isMobile && (
                  <div className="feature-image">
                    <img src={feature_candidate_1} alt="Feature Candidate 1" className="feature-image__img" />
                  </div>
                )}
                <ul className="feature-list">
                  <li className="feature-list-item">
                    <span className="feature-list-explain-icon"></span>
                    <span>Over 90% of applications undergo ATS keyword filtering and ranking. Your resume won't get to the recruiter if it doesn't pass this step.</span>
                  </li>
                  <li className="feature-list-item">
                    <span className="feature-list-explain-icon"></span>
                    <span>ChatGPT only finds 50% of keywords, for one job at a time, and writes resumes that scream AI-written.</span>
                  </li>
                  <li className="feature-list-item">
                    <span className="feature-list-explain-icon"></span>
                    <span>GimmeResume studies recent job postings and identifies all the keywords you need to get past the ATS.</span>
                  </li>
                </ul>
              </div>
              {!isMobile && (
                <div className="feature-image">
                  <img src={feature_candidate_1} alt="Feature Candidate 1" className="feature-image__img" />
                </div>
              )}
            </div>

            {/* Second section - image on left, content on right */}
            <div className="feature-container">
              {!isMobile && (
                <div className="feature-image">
                  <img src={feature_candidate_3} alt="Feature Candidate 3" className="feature-image__img" />
                </div>
              )}
              <div className="feature-content">
                <div className="feature-badge">

                  <span>
                    <FeatureBadgeIcon className="feature-badge-icon" />
                    Proven results
                  </span>
                </div>
                <h3 className="feature-title">Join thousands who've landed their dream roles</h3>
                {isMobile && (
                  <div className="feature-image">
                    <img src={feature_candidate_3} alt="Feature Candidate 3" className="feature-image__img" />
                  </div>
                )}
                <ul className="feature-list">
                  <li className="feature-list-item">
                    <span className="feature-list-explain-icon"></span>
                    <span>GimmeResume writes a recruiter-optimized resume that doesn't read like AI.</span>
                  </li>
                  <li className="feature-list-item">
                    <span className="feature-list-explain-icon"></span>
                    <span>Skills are mapped to US standardized keywords to ensure the highest ATS detection and ranking.</span>
                  </li>
                  <li className="feature-list-item">
                    <span className="feature-list-explain-icon"></span>
                    <span>Get your optimized resume in under 60 seconds.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Third section - content on left, image on right */}
            <div className="feature-container">
              <div className="feature-content">
                <div className="feature-badge">

                  <span>
                    <FeatureBadgeIcon className="feature-badge-icon" />
                    Easy as 1-2-3
                  </span>
                </div>
                <h3 className="feature-title">Start ranking higher in seconds</h3>
                {isMobile && (
                  <div className="feature-image">
                    <img src={feature_candidate_4} alt="Feature Candidate 4" className="feature-image__img" />
                  </div>
                )}
                <ul className="feature-list">
                  <li className="feature-list-item">
                    <span className="feature-list-explain-icon"></span>
                    <span>Just upload and go - no complex forms or lengthy questionnaires.</span>
                  </li>
                  <li className="feature-list-item">
                    <span className="feature-list-explain-icon"></span>
                    <span>Discover the in-demand keywords that are missing so you can either add them with a simple click.</span>
                  </li>
                  <li className="feature-list-item">
                    <span className="feature-list-explain-icon"></span>
                    <span>Make any final edits to your optimized resume before downloading.</span>
                  </li>
                </ul>
              </div>

              {!isMobile && (
                <div className="feature-image">
                  <img src={feature_candidate_4} alt="Feature Candidate 4" className="feature-image__img" />
                </div>
              )}
            </div>

            <div className="cta-container">
              <button className="btn-primary" onClick={() => { window.plausible('CTA Click', { props: { location: 'middle' } }); scrollToOptimize(); }}>
                Optimize My Resume
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="section testimonials-section">
        <div className="testimonials-container">
          <div className="card testimonial-card">
            <div className="testimonial-content">
              <TestimonialQuote className="testimonial-quote-icon" />
              <div className="testimonial-body">
                <div className="testimonial-title">My resume got me the interview!</div>
                <p className="testimonial-text">
                  GimmeResume helped me identify the keywords I was missing for my dream job at JPMorgan. It's addressing a pertinent problem in the job market!
                </p>
              </div>
            </div>

            <div className="testimonial-footer">
              <div className="testimonial-name">Afnan Khan</div>
              <div className="testimonial-stars" aria-label="5 out of 5 stars">★★★★★</div>
              <div className="testimonial-date">1 month ago</div>
            </div>
          </div>

          <div className="card testimonial-card">
            <div className="testimonial-content">
              <TestimonialQuote className="testimonial-quote-icon" />
              <div className="testimonial-body">
                <div className="testimonial-title">I can't believe how good this is</div>
                <p className="testimonial-text">
                  It was super easy to navigate and this is the cleanest resume I've ever seen.
                </p>
              </div>
            </div>

            <div className="testimonial-footer">
              <div className="testimonial-name">Johnathan Stanley</div>
              <div className="testimonial-stars" aria-label="5 out of 5 stars">★★★★★</div>
              <div className="testimonial-date">1 month ago</div>
            </div>
          </div>

          <div className="card testimonial-card">
            <div className="testimonial-content">
              <TestimonialQuote className="testimonial-quote-icon" />
              <div className="testimonial-body">
                <div className="testimonial-title">Took care of what matters</div>
                <p className="testimonial-text">
                  I had so much trouble deciding what should go on my resume. GimmeResume kept it concise while bringing out the important points relevant to my job title.
                </p>
              </div>
            </div>

            <div className="testimonial-footer">
              <div className="testimonial-name">Alice Kuang</div>
              <div className="testimonial-stars" aria-label="5 out of 5 stars">★★★★★</div>
              <div className="testimonial-date">1 month ago</div>
            </div>
          </div>
          {isMobile && (
            <div className="cta-container">
              <button className="btn-primary" onClick={() => { window.plausible('CTA Click', { props: { location: 'middle' } }); scrollToOptimize(); }}>
                Optimize My Resume
              </button>
            </div>
          )}
        </div>

      </div>

      <div className="white-bg-section">
        <div className="section-inner faq-section-outer" id="faq">
          <div className="section-inner faq-section">
            <h2 className="section-title faq-section-title">
              Frequently Asked Questions
            </h2>
            {!isMobile && (
              <div className="faq-grid">
                {/* Left Column - odd indexed items */}
                <div className="faq-column">
                  {FAQ_DATA.filter((_, index) => index % 2 === 0).map((faq) => (
                    <div className="faq-item" key={faq.id}>
                      <div
                        className={`faq-question-container ${expandedFaqs[faq.id] ? 'expanded' : ''}`}
                        onClick={() => toggleFaq(faq.id)}
                      >
                        <h3 className="faq-question">{faq.question}</h3>
                        <div className="faq-toggle-icon">{expandedFaqs[faq.id] ? '−' : '+'}</div>
                      </div>
                      <div className={`faq-answer ${expandedFaqs[faq.id] ? 'expanded' : ''}`}>
                        <p>{faq.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Right Column - even indexed items */}
                <div className="faq-column">
                  {FAQ_DATA.filter((_, index) => index % 2 === 1).map((faq) => (
                    <div className="faq-item" key={faq.id}>
                      <div
                        className={`faq-question-container ${expandedFaqs[faq.id] ? 'expanded' : ''}`}
                        onClick={() => toggleFaq(faq.id)}
                      >
                        <h3 className="faq-question">{faq.question}</h3>
                        <div className="faq-toggle-icon">{expandedFaqs[faq.id] ? '−' : '+'}</div>
                      </div>
                      <div className={`faq-answer ${expandedFaqs[faq.id] ? 'expanded' : ''}`}>
                        <p>{faq.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {isMobile && (
              <div className="faq-grid">
                {/* Left Column - odd indexed items */}
                <div className="faq-column">
                  {FAQ_DATA.filter((_, index) => index).map((faq) => (
                    <div className="faq-item" key={faq.id}>
                      <div
                        className={`faq-question-container ${expandedFaqs[faq.id] ? 'expanded' : ''}`}
                        onClick={() => toggleFaq(faq.id)}
                      >
                        <h3 className="faq-question">{faq.question}</h3>
                        <div className="faq-toggle-icon">{expandedFaqs[faq.id] ? '−' : '+'}</div>
                      </div>
                      <div className={`faq-answer ${expandedFaqs[faq.id] ? 'expanded' : ''}`}>
                        <p>{faq.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="green-background-section">
        <div className="section cta-section">
          <h2 className="cta-title">
            Ready to be seen?
          </h2>
          <p className="cta-description">
            Distill your resume with intelligence trained on the entire job market. ChatGPT makes you lose, GimmeResume lets you win.
          </p>
          <div className="cta-container">
            <button className="btn-primary" onClick={() => { window.plausible('CTA Click', { props: { location: 'bottom' } }); scrollToOptimize(); }}>
              Optimize My Resume
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="result-container error">
          {error}
        </div>
      )}

      {isVideoOpen && (
        <div
          className="video-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="How It Works video"
          onClick={closeHowItWorksVideo}
        >
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="video-modal-close"
              onClick={closeHowItWorksVideo}
              aria-label="Close video"
            >
              ×
            </button>
            {videoLoadError && (
              <p className="video-modal-error" role="alert">
                The video could not be loaded. If this keeps happening, try a different network or open this page on a
                laptop or desktop browser.
              </p>
            )}
            <video
              ref={videoRef}
              className={`video-modal-player${videoLoadError ? ' video-modal-player--hidden' : ''}`}
              controls
              autoPlay
              playsInline
              preload="metadata"
              onError={() => setVideoLoadError(true)}
            >
              <source src={HOW_IT_WORKS_VIDEO_SRC} type="video/mp4" />
              Sorry, your browser doesn&apos;t support embedded videos.
            </video>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;